import { useState } from "react";
import PremiumLayout from "./PremiumLayout";
import { http } from "../../lib/api";
import { toast } from "sonner";

const SERVICES = ["Civil & Structural", "Interior Architecture", "Turnkey Project", "Just exploring"];

// Bengaluru MG Road area coordinates — matches the seeded company address.
// OpenStreetMap embed with a dark tile style rendered as a mask overlay.
const MAP_LAT = 12.9716;
const MAP_LNG = 77.5946;
const MAP_BBOX = `${MAP_LNG - 0.01},${MAP_LAT - 0.007},${MAP_LNG + 0.01},${MAP_LAT + 0.007}`;
const MAP_SRC = `https://www.openstreetmap.org/export/embed.html?bbox=${MAP_BBOX}&layer=mapnik&marker=${MAP_LAT},${MAP_LNG}`;

export default function PremiumContact() {
  const [form, setForm] = useState({ name: "", location: "", service: SERVICES[0], phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Name and a short message are required");
      return;
    }
    setBusy(true);
    try {
      await http.post("/requests", {
        client_name: form.name.trim(),
        client_contact: [form.phone, form.email].filter(Boolean).join(" · "),
        message: `[${form.service}] · ${form.location ? form.location + " · " : ""}${form.message}`,
      });
      setSent(true);
      toast.success("Thanks — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PremiumLayout>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-8">
        <div className="mono gold">Consultation</div>
        <h1 className="serif text-5xl md:text-7xl leading-[1.02] mt-4 cream">
          Book a design <span className="gold italic">session.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-[#B8B0A0] text-lg">
          A 45-minute conversation, on us. We&apos;ll walk you through what your project could be —
          scope, sequence, budget honesty — and you&apos;ll leave with a clear next step.
        </p>
      </section>

      {/* Form + info split */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid md:grid-cols-2 gap-12">
        <form onSubmit={submit} data-testid="premium-contact-form" className="space-y-6">
          <Field label="Your Name">
            <input required data-testid="pc-name" value={form.name} onChange={set("name")}
                   className="w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#C9A96E] outline-none py-3 cream" />
          </Field>
          <Field label="Location / City">
            <input data-testid="pc-location" value={form.location} onChange={set("location")}
                   className="w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#C9A96E] outline-none py-3 cream" />
          </Field>
          <Field label="Service Needed">
            <select data-testid="pc-service" value={form.service} onChange={set("service")}
                    className="w-full bg-[#0A0A0A] border-b border-[#2A2A2A] focus:border-[#C9A96E] outline-none py-3 cream">
              {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Phone">
              <input data-testid="pc-phone" value={form.phone} onChange={set("phone")}
                     className="w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#C9A96E] outline-none py-3 cream" />
            </Field>
            <Field label="Email">
              <input data-testid="pc-email" type="email" value={form.email} onChange={set("email")}
                     className="w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#C9A96E] outline-none py-3 cream" />
            </Field>
          </div>
          <Field label="Tell us about your project">
            <textarea required data-testid="pc-message" rows={4} value={form.message} onChange={set("message")}
                      className="w-full bg-transparent border border-[#2A2A2A] focus:border-[#C9A96E] outline-none p-4 cream" />
          </Field>
          <button type="submit" disabled={busy || sent}
                  data-testid="pc-submit"
                  className="mono border border-[#C9A96E] gold px-8 py-4 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors disabled:opacity-50">
            {sent ? "Sent — thank you." : busy ? "Sending…" : "Request Session →"}
          </button>
        </form>

        <aside className="space-y-10">
          <div>
            <div className="mono gold">Studio</div>
            <div className="serif text-2xl cream mt-3 leading-relaxed">
              Studio DHP, MG Road<br />Bengaluru, KA 560001
            </div>
          </div>
          <div>
            <div className="mono gold">Reach us directly</div>
            <div className="mt-3 space-y-1 text-[#DDD5C4]">
              <div>+91 90000 00000</div>
              <div><a href="mailto:hello@dreamhomeplanners.com" className="gold-link">hello@dreamhomeplanners.com</a></div>
              <div>Mon–Sat · 10:00–19:00</div>
            </div>
          </div>
          <div>
            <div className="mono gold">Response time</div>
            <p className="mt-3 text-[#B8B0A0] text-sm leading-relaxed">
              We reply to every enquiry within 24 hours — usually within four. If the project is a fit,
              our next step is a no-cost site visit or a call with the studio principal.
            </p>
          </div>
        </aside>
      </section>

      {/* Dark themed map — greyscale filter on OpenStreetMap iframe */}
      <section className="mt-4 border-t border-[#1F1F1F]" data-testid="premium-contact-map">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
          <div className="mono gold">Find the studio</div>
          <h2 className="serif text-4xl md:text-5xl cream mt-3">Where good buildings begin.</h2>
        </div>
        <div className="relative w-full h-[420px] overflow-hidden border-t border-b border-[#1F1F1F]">
          <iframe
            title="Studio DHP location"
            src={MAP_SRC}
            className="absolute inset-0 w-full h-full"
            style={{ filter: "invert(0.92) hue-rotate(180deg) grayscale(0.4) brightness(0.85) contrast(1.05)" }}
            loading="lazy"
          />
          <a href={`https://www.openstreetmap.org/?mlat=${MAP_LAT}&mlon=${MAP_LNG}#map=16/${MAP_LAT}/${MAP_LNG}`}
             target="_blank" rel="noreferrer"
             data-testid="premium-map-open"
             className="absolute bottom-6 left-6 mono gold border border-[#C9A96E] bg-[#0A0A0A]/85 px-4 py-2 hover:bg-[#C9A96E] hover:text-[#0A0A0A]">
            Open in Maps ↗
          </a>
        </div>
      </section>
    </PremiumLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mono text-[#9A9284] mb-2">{label}</div>
      {children}
    </label>
  );
}
